import { z } from 'zod';

// ── Action types ───────────────────────────────────────────────────────────────

export const ApplicationActionSchema = z.enum([
  'applied',
  'phone_screen',
  'interview',
  'technical_interview',
  'offer',
  'accepted',
  'rejected',
  'declined',
  'ghosted',
  'withdrawn',
]);

export type ApplicationAction = z.infer<typeof ApplicationActionSchema>;

export const APPLICATION_ACTION_LABELS: Record<ApplicationAction, string> = {
  applied:               'Applied',
  phone_screen:          'Phone Screen',
  interview:             'Interview',
  technical_interview:   'Technical Interview',
  offer:                 'Offer',
  accepted:              'Accepted',
  rejected:              'Rejected',
  declined:              'Declined',
  ghosted:               'Ghosted',
  withdrawn:             'Withdrawn',
};

// ── Full application (server → client) ────────────────────────────────────────

export const ApplicationSchema = z.object({
  id:             z.string().uuid(),
  userId:         z.string().uuid(),
  postingId:      z.string().uuid().nullable(),
  postingUrl:     z.string().nullable(),
  company:        z.string(),
  jobTitle:       z.string(),
  appliedDate:    z.string(),  // YYYY-MM-DD
  lastAction:     ApplicationActionSchema,
  lastActionDate: z.string(),  // YYYY-MM-DD
  notes:          z.string().nullable(),
  createdAt:      z.string().datetime(),
  updatedAt:      z.string().datetime(),
});

export type Application = z.infer<typeof ApplicationSchema>;

// ── Create request (client → server) ──────────────────────────────────────────

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Must be a date (YYYY-MM-DD)');

export const CreateApplicationSchema = z.object({
  postingId:      z.string().uuid().optional(),
  postingUrl:     z.string().optional(),
  company:        z.string().min(1, 'Company is required'),
  jobTitle:       z.string().min(1, 'Job title is required'),
  appliedDate:    isoDate,
  lastAction:     ApplicationActionSchema.optional().default('applied'),
  lastActionDate: isoDate,
  notes:          z.string().optional(),
});

export type CreateApplication = z.infer<typeof CreateApplicationSchema>;

// ── Update request (client → server) ──────────────────────────────────────────

export const UpdateApplicationSchema = z.object({
  postingUrl:     z.string().nullable().optional(),
  company:        z.string().min(1).optional(),
  jobTitle:       z.string().min(1).optional(),
  appliedDate:    isoDate.optional(),
  lastAction:     ApplicationActionSchema.optional(),
  lastActionDate: isoDate.optional(),
  notes:          z.string().nullable().optional(),
});

export type UpdateApplication = z.infer<typeof UpdateApplicationSchema>;

// ── Application event ──────────────────────────────────────────────────────────

export const ApplicationEventSchema = z.object({
  id:            z.string().uuid(),
  applicationId: z.string().uuid(),
  userId:        z.string().uuid(),
  action:        ApplicationActionSchema,
  eventDate:     z.string(), // YYYY-MM-DD
  createdAt:     z.string().datetime(),
});

export type ApplicationEvent = z.infer<typeof ApplicationEventSchema>;

export const UpdateApplicationEventSchema = z.object({
  action:    ApplicationActionSchema.optional(),
  eventDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Must be YYYY-MM-DD').optional(),
});

export type UpdateApplicationEvent = z.infer<typeof UpdateApplicationEventSchema>;

// ── User company metadata ──────────────────────────────────────────────────────

export const UserCompanySchema = z.object({
  id:          z.string().uuid(),
  userId:      z.string().uuid(),
  companyName: z.string(),
  portalUrl:   z.string().nullable(),
  createdAt:   z.string().datetime(),
  updatedAt:   z.string().datetime(),
});

export type UserCompany = z.infer<typeof UserCompanySchema>;

export const UpdateUserCompanySchema = z.object({
  portalUrl: z.string().nullable().optional(),
});

export type UpdateUserCompany = z.infer<typeof UpdateUserCompanySchema>;
